// ColdOutreachQueue.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import ReactPaginate from "react-paginate";
import ReactModal from "react-modal";
import Select from "react-select";
import { toast } from "react-toastify";
import {
    Loader2,
    Search,
    Filter,
    RefreshCw,
    Pencil,
    Trash2,
    X,
    ExternalLink,
} from "lucide-react";

const OP_OPTIONS = [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Does not equal" },
    { value: "has", label: "Contains data" },
    { value: "not_has", label: "Does not contain data" },
    { value: "contains", label: "Contains" },
    { value: "not_contains", label: "Does not contain" },
    { value: "begins_with", label: "Begins with" },
    { value: "not_begins_with", label: "Does not begin with" },
    { value: "ends_with", label: "Ends with" },
    { value: "not_ends_with", label: "Does not end with" },
];

const isNoop = (f) => !f || (f.op === "equals" && (!f.values || f.values.length === 0));
const isDateField = (f) => /(_at|_time)$/.test(f);
const toDate = (v) => {
    if (!v) return null;
    const s = typeof v === "string" ? v.replace(/ GMT$/, "") : v;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};
const fmt = (d) => {
    if (!d) return "-";
    const dt = new Date(String(d).replace(/ GMT$/, ""));
    const day = String(dt.getDate()).padStart(2, "0");
    const mon = dt.toLocaleString("en-AU", { month: "short" });
    const yr = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, "0");
    const mm = String(dt.getMinutes()).padStart(2, "0");
    return `${day} ${mon}, ${yr} ${hh}:${mm}`;
};

// --- Shared react-select style (44px controls) ---
const selectStyles44 = {
    control: (p) => ({
        ...p,
        minHeight: 44,
        height: 44,
        borderColor: "#e2e8f0",
        boxShadow: "none",
        borderRadius: 12,
        "&:hover": { borderColor: "#cbd5e1" },
    }),
    valueContainer: (p) => ({ ...p, height: 44 }),
    input: (p) => ({ ...p, margin: 0, padding: 0 }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
        color: s.isSelected ? "white" : "#0f172a",
    }),
    menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const badgeForStatus = (s) => {
    const v = String(s || "").toLowerCase();
    const base = "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset";
    if (v === "pending") return `${base} bg-amber-50 text-amber-700 ring-amber-600/20`;
    if (v === "processing") return `${base} bg-sky-50 text-sky-700 ring-sky-600/20`;
    if (v === "completed") return `${base} bg-emerald-50 text-emerald-700 ring-emerald-600/20`;
    if (v === "failed") return `${base} bg-rose-50 text-rose-700 ring-rose-600/20`;
    if (v === "cancelled") return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
    return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
};

const badgeForStage = (s) => {
    const v = String(s || "").toLowerCase();
    const base = "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset";
    if (v === "social_touch") return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
    if (v === "email_verification") return `${base} bg-sky-50 text-sky-700 ring-sky-600/20`;
    if (v === "email_drafted") return `${base} bg-indigo-50 text-indigo-700 ring-indigo-600/20`;
    if (v === "email_sent") return `${base} bg-emerald-50 text-emerald-700 ring-emerald-600/20`;
    if (v === "unmatched") return `${base} bg-rose-50 text-rose-700 ring-rose-600/20`;
    return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
};

const badgeForOutcome = (s) => {
    const v = String(s || "").toLowerCase();
    const base = "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset";
    if (!v) return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
    if (v === "no_answer") return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
    if (v === "zero_interest") return `${base} bg-rose-50 text-rose-700 ring-rose-600/20`;
    if (v === "neutral_interest") return `${base} bg-amber-50 text-amber-700 ring-amber-600/20`;
    if (v === "positive_interest") return `${base} bg-emerald-50 text-emerald-700 ring-emerald-600/20`;
    return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
};

const FilterPopover = ({ btnText, filter, setFilter, options }) => {
    const [open, setOpen] = useState(false);
    const [draftOp, setDraftOp] = useState(filter.op);
    const [draftVals, setDraftVals] = useState(filter.values || []);
    const ref = useRef(null);

    useEffect(() => {
        if (open) {
            setDraftOp(filter.op);
            setDraftVals(filter.values || []);
        }
    }, [open, filter.op, filter.values]);

    useEffect(() => {
        const onDoc = (e) => {
            if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const disabled = draftOp === "has" || draftOp === "not_has";

    const pillBtn =
        "inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20";

    return (
        <div className="relative">
            <button type="button" className={pillBtn} onClick={() => setOpen((o) => !o)}>
                <Filter className="h-4 w-4 text-slate-600" />
                {btnText}
                {!isNoop(filter) && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-bold text-white">
                        ON
                    </span>
                )}
            </button>

            {open && (
                <div
                    ref={ref}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="absolute right-0 mt-2 w-[22rem] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl z-20"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-sm font-extrabold text-slate-900">Filter: {btnText}</div>
                            <div className="mt-0.5 text-xs text-slate-500">Choose operator and values.</div>
                        </div>
                        <button
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                            onClick={() => setOpen(false)}
                            aria-label="Close popover"
                            title="Close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="mt-3 space-y-3">
                        <Select
                            value={OP_OPTIONS.find((o) => o.value === draftOp)}
                            onChange={(op) => setDraftOp(op.value)}
                            options={OP_OPTIONS}
                            classNamePrefix="react-select"
                            placeholder="Equals"
                            styles={selectStyles44}
                            menuPortalTarget={document.body}
                        />

                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isDisabled={disabled}
                            value={draftVals}
                            onChange={(vals) => setDraftVals(vals || [])}
                            options={options}
                            classNamePrefix="react-select"
                            placeholder={disabled ? "Not required for this operator" : "Select..."}
                            isClearable={false}
                            styles={selectStyles44}
                            menuPortalTarget={document.body}
                        />
                    </div>

                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                            onClick={() => setDraftVals([])}
                        >
                            Clear
                        </button>
                        <button
                            className="inline-flex h-11 items-center justify-center rounded-2xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/30"
                            onClick={() => {
                                setFilter({ op: draftOp, values: draftVals });
                                setOpen(false);
                            }}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function ColdOutreachQueue() {
    const [rows, setRows] = useState([]);

    const DEFAULT_STATUS = { value: "", label: "All statuses" };
    const statusOptions = [
        { value: "", label: "All statuses" },
        { value: "pending", label: "Pending" },
        { value: "processing", label: "Processing" },
        { value: "completed", label: "Completed" },
        { value: "failed", label: "Failed" },
        { value: "cancelled", label: "Cancelled" },
    ];

    const stageOptions = [
        { value: "social_touch", label: "Social Touch" },
        { value: "email_verification", label: "Email Verification" },
        { value: "email_drafted", label: "Email Drafted" },
        { value: "email_sent", label: "Email Sent" },
        { value: "unmatched", label: "Unmatched" },
    ];

    const outcomeOptions = [
        { value: "", label: "—" },
        { value: "no_answer", label: "No answer" },
        { value: "zero_interest", label: "Zero interest" },
        { value: "neutral_interest", label: "Neutral interest" },
        { value: "positive_interest", label: "Positive interest" },
    ];

    // Contact status (from Contact.status) if you want to filter it too
    const contactStatusOptions = [
        { value: "", label: "All contact statuses" },
        { value: "connected", label: "Connected" },
        { value: "unconnected", label: "Unconnected" },
        { value: "unknown", label: "Unknown" },
    ];

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);

    const [stageFilter, setStageFilter] = useState({ op: "equals", values: [] });
    const [outcomeFilter, setOutcomeFilter] = useState({ op: "equals", values: [] });
    const [contactStatusFilter, setContactStatusFilter] = useState({ op: "equals", values: [] });

    const [currentPage, setCurrentPage] = useState(0);
    const [sortField, setSortField] = useState("domain_outreach_score");
    const [sortOrder, setSortOrder] = useState("desc");

    const [modalType, setModalType] = useState(null);
    const [modalRecord, setModalRecord] = useState(null);

    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const recordsPerPage = 30;

    const encodeVals = (vals = []) =>
        vals
            .map((v) => (v?.value ?? "").toString().trim())
            .filter(Boolean)
            .join(",");

    const fetchQueue = async (pageToLoad = currentPage + 1) => {
        const safePage =
            typeof pageToLoad === "number" && Number.isFinite(pageToLoad)
                ? pageToLoad
                : currentPage + 1;
        try {
            setLoading(true);
            const resp = await axiosInstance.get("/queue/outreachQueue", {
                params: {
                    page: safePage,
                    page_size: recordsPerPage,
                    sort_by: sortField || "domain_outreach_score",
                    sort_order: sortOrder || "desc",
                    status: statusFilter.value || undefined,
                    q: searchTerm.trim() || undefined,
                    stage_op: isNoop(stageFilter) ? undefined : stageFilter.op,
                    stage_vals: isNoop(stageFilter) ? undefined : encodeVals(stageFilter.values),
                    outcome_op: isNoop(outcomeFilter) ? undefined : outcomeFilter.op,
                    outcome_vals: isNoop(outcomeFilter) ? undefined : encodeVals(outcomeFilter.values),
                    contact_status_op: isNoop(contactStatusFilter) ? undefined : contactStatusFilter.op,
                    contact_status_vals: isNoop(contactStatusFilter) ? undefined : encodeVals(contactStatusFilter.values),
                },
            });
            const payload = resp?.data || {};
            setRows(Array.isArray(payload?.items) ? payload.items : []);
            setTotalPages(Number(payload?.total_pages || 1));
            setTotalRecords(Number(payload?.total || 0));
        } catch (e) {
            console.error(e);
            toast.error("Failed to fetch cold outreach queue.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQueue(currentPage + 1);
    }, [
        currentPage,
        sortField,
        sortOrder,
        statusFilter.value,
        searchTerm,
        stageFilter,
        outcomeFilter,
        contactStatusFilter,
    ]);

    useEffect(() => {
        setSearchTerm("");
        setSortField("domain_outreach_score");
        setSortOrder("desc");
        setStageFilter({ op: "equals", values: [] });
        setOutcomeFilter({ op: "equals", values: [] });
        setContactStatusFilter({ op: "equals", values: [] });
        setStatusFilter(DEFAULT_STATUS);
    }, []);

    const hasFilters =
        searchTerm.trim() !== "" ||
        statusFilter.value !== "" ||
        (!isNoop(stageFilter) || !isNoop(outcomeFilter) || !isNoop(contactStatusFilter));

    const stageOptionsForFilter = useMemo(() => stageOptions.map((s) => ({ value: s.value, label: s.label })), []);
    const outcomeOptionsForFilter = useMemo(() => outcomeOptions.filter(o => o.value !== "").map((o) => ({ value: o.value, label: o.label })), []);
    const contactStatusOptionsForFilter = useMemo(() => contactStatusOptions.filter(o => o.value !== "").map((o) => ({ value: o.value, label: o.label })), []);

    const pageCount = totalPages || 0;
    const displayed = rows;

    const handleSort = (field) => {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder("asc");
        } else if (sortOrder === "asc") setSortOrder("desc");
        else {
            setSortField(null);
            setSortOrder("asc");
        }
    };

    const openModal = (type, record) => {
        setModalType(type);
        setModalRecord(record);
    };
    const closeModal = () => {
        setModalType(null);
        setModalRecord(null);
    };

    const handleModalChange = (e) => {
        const { name, value } = e.target;
        setModalRecord((prev) => ({ ...prev, [name]: value }));
    };

    const handleCancel = async () => {
        const id = modalRecord?.outreach_queue_id;
        if (!id) return;
        try {
            await axiosInstance.delete(`/queue/outreachQueue/${id}`);
            toast.success("Cancelled successfully!");
            fetchQueue();
            closeModal();
        } catch {
            toast.error("Failed to cancel item.");
        }
    };

    const handleUpdate = async () => {
        const id = modalRecord?.outreach_queue_id;
        if (!id) return;

        const payload = {
            stage: modalRecord?.stage,
            status: modalRecord?.status,
            outcome: modalRecord?.outcome || null,
        };

        try {
            await axiosInstance.put(`/queue/outreachQueue/${id}`, payload);
            toast.success("Updated successfully!");
            fetchQueue();
            closeModal();
        } catch {
            toast.error("Failed to update item.");
        }
    };

    const renderFields = (record, editable = false) => {
        const statusOptionsModal = statusOptions.filter((o) => o.value !== "");
        const defs = [
            { label: "Contact Name", name: "contact_name", type: "text", readOnly: true },
            { label: "Company Name", name: "company_name", type: "text", readOnly: true },
            { label: "Position", name: "position", type: "text", readOnly: true },
            { label: "AI Position", name: "ai_position", type: "text", readOnly: true },
            { label: "LinkedIn Email", name: "email", type: "email", readOnly: true },
            { label: "Business Email", name: "business_email", type: "email", readOnly: true },
            { label: "Business Email Status", name: "business_email_status", type: "text", readOnly: true },
            { label: "Phone", name: "phone", type: "text", readOnly: true },
            { label: "Profile URL", name: "profile_url", type: "url", isLink: true, readOnly: true },
            { label: "Follower Count", name: "follower_count", type: "text", readOnly: true },
            { label: "Company Website", name: "company_website", type: "url", isLink: true, readOnly: true },
            { label: "Company LinkedIn", name: "company_linkedin", type: "url", isLink: true, readOnly: true },
            { label: "Sales Nav Link", name: "sales_nav_link", type: "url", isLink: true, readOnly: true },

            // outreach fields
            { label: "Domain", name: "domain", type: "text", readOnly: true },
            { label: "Domain Outreach Score", name: "domain_outreach_score", type: "number", readOnly: true },
            { label: "Stage", name: "stage", type: "select", options: stageOptions },
            // { label: "Status", name: "status", type: "select", options: statusOptionsModal },
            { label: "Outcome", name: "outcome", type: "select", options: outcomeOptions },
            { label: "Created At", name: "created_at", type: "text", readOnly: true },
            { label: "Updated At", name: "updated_at", type: "text", readOnly: true },
            { label: "Completed At", name: "completed_at", type: "text", readOnly: true },
        ];

        return defs.map((f) => {
            const val = record[f.name] ?? "";
            const isDate = ["created_at", "updated_at", "completed_at"].includes(f.name);

            if (isDate) {
                return (
                    <div key={f.name} className="space-y-1">
                        <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                        <div className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-700">
                            {fmt(val)}
                        </div>
                    </div>
                );
            }

            if (editable && !f.readOnly) {
                if (f.type === "select") {
                    return (
                        <div key={f.name} className="space-y-1">
                            <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                            <Select
                                name={f.name}
                                value={(f.options || []).find((o) => o.value === String(val))}
                                onChange={(opt) => setModalRecord((prev) => ({ ...prev, [f.name]: opt?.value }))}
                                options={f.options || []}
                                classNamePrefix="react-select"
                                styles={selectStyles44}
                                menuPortalTarget={document.body}
                            />
                        </div>
                    );
                }

                return (
                    <div key={f.name} className="space-y-1">
                        <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                        <input
                            name={f.name}
                            type={f.type || "text"}
                            value={val}
                            onChange={handleModalChange}
                            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400"
                        />
                    </div>
                );
            }

            if (f.isLink) {
                const href = val?.startsWith("http") ? val : val ? `https://${val}` : "#";
                return (
                    <div key={f.name} className="space-y-1">
                        <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex h-11 items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-900 hover:bg-slate-100"
                        >
                            <span className="truncate">{val ? String(val) : "-"}</span>
                            <ExternalLink className="h-4 w-4 text-slate-500 group-hover:text-slate-700" />
                        </a>
                    </div>
                );
            }

            return (
                <div key={f.name} className="space-y-1">
                    <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap">
                        {String(val || "-")}
                    </div>
                </div>
            );
        });
    };

    const columns = [
        { label: "Contact Name", field: "contact_name" },
        { label: "Company", field: "company_name" },
        { label: "Position", field: "position" },
        { label: "Business Email", field: "business_email" },
        { label: "Stage", field: "stage" },
        // { label: "Status", field: "status" },
        { label: "Outcome", field: "outcome" },
        { label: "Score", field: "domain_outreach_score" },
    ];

    const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";
    const iconBtn =
        "inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:opacity-50";

    const subtleBtn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:opacity-50";

    const warnBtn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-amber-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500/30 disabled:opacity-50";

    const dangerBtn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-rose-700 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-600/30 disabled:opacity-50";

    const headerNoteClass =
        "rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between";

    return (
        <>
            {/* Controls */}
            <div className={`${cardClass} p-4`}>
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                    <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                        {/* Search */}
                        <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search contact, company, email, position..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(0);
                                }}
                                className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/20"
                            />
                        </div>

                        <div className="w-full lg:w-auto">
                            {/* Mobile */}
                            <div className="lg:hidden space-y-2">
                                <div className="w-full relative">
                                    <FilterPopover
                                        btnText="Stage"
                                        filter={stageFilter}
                                        setFilter={(v) => {
                                            setStageFilter(v);
                                            setCurrentPage(0);
                                        }}
                                        options={stageOptionsForFilter}
                                    />
                                </div>

                                <div className="w-full relative">
                                    <FilterPopover
                                        btnText="Outcome"
                                        filter={outcomeFilter}
                                        setFilter={(v) => {
                                            setOutcomeFilter(v);
                                            setCurrentPage(0);
                                        }}
                                        options={outcomeOptionsForFilter}
                                    />
                                </div>

                                <div className="w-full relative">
                                    <FilterPopover
                                        btnText="Contact status"
                                        filter={contactStatusFilter}
                                        setFilter={(v) => {
                                            setContactStatusFilter(v);
                                            setCurrentPage(0);
                                        }}
                                        options={contactStatusOptionsForFilter}
                                    />
                                </div>

                                {/* <div className="w-full">
                                    <Select
                                        value={statusFilter}
                                        onChange={(v) => {
                                            setStatusFilter(v || DEFAULT_STATUS);
                                            setCurrentPage(0);
                                        }}
                                        options={statusOptions}
                                        classNamePrefix="react-select"
                                        placeholder="Status"
                                        styles={selectStyles44}
                                        menuPortalTarget={document.body}
                                    />
                                </div> */}
                            </div>

                            {/* Desktop */}
                            <div className="hidden lg:flex flex-wrap items-center gap-2">
                                <FilterPopover
                                    btnText="Stage"
                                    filter={stageFilter}
                                    setFilter={(v) => {
                                        setStageFilter(v);
                                        setCurrentPage(0);
                                    }}
                                    options={stageOptionsForFilter}
                                />
                                <FilterPopover
                                    btnText="Outcome"
                                    filter={outcomeFilter}
                                    setFilter={(v) => {
                                        setOutcomeFilter(v);
                                        setCurrentPage(0);
                                    }}
                                    options={outcomeOptionsForFilter}
                                />
                                <FilterPopover
                                    btnText="Contact status"
                                    filter={contactStatusFilter}
                                    setFilter={(v) => {
                                        setContactStatusFilter(v);
                                        setCurrentPage(0);
                                    }}
                                    options={contactStatusOptionsForFilter}
                                />
                                {/* <div className="min-w-[220px]">
                                    <Select
                                        value={statusFilter}
                                        onChange={(v) => {
                                            setStatusFilter(v || DEFAULT_STATUS);
                                            setCurrentPage(0);
                                        }}
                                        options={statusOptions}
                                        classNamePrefix="react-select"
                                        placeholder="Status"
                                        styles={selectStyles44}
                                        menuPortalTarget={document.body}
                                    />
                                </div> */}
                            </div>
                        </div>
                    </div>

                    <button onClick={() => fetchQueue(currentPage + 1)} disabled={loading} className={subtleBtn} aria-busy={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Refresh
                    </button>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm text-slate-600">
                        {hasFilters ? `Showing ${displayed.length} of ${totalRecords} total` : `Total records: ${totalRecords}`}
                    </div>
                    <div className="text-xs text-slate-500">
                        Sort:{" "}
                        <span className="font-semibold text-slate-700">
                            {sortField ? `${sortField} (${sortOrder})` : "none"}
                        </span>
                    </div>
                </div>
            </div>

            {/* Note */}
            <div className={`mt-4 ${headerNoteClass}`}>
                <div className="flex items-center gap-2">
                    <span className="inline-flex h-7 items-center rounded-full bg-slate-900 px-2.5 text-xs font-bold text-white">
                        Note
                    </span>
                    <span>
                        Daily top-up keeps the list starting at <b>100</b> contact leads, sorted by <b>domain outreach score</b>.
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span className={badgeForStage("social_touch")}>Social Touch</span>
                    <span className={badgeForStage("email_verification")}>Email Verification</span>
                    <span className={badgeForStage("email_drafted")}>Email Drafted</span>
                    <span className={badgeForStage("email_sent")}>Email Sent</span>
                    <span className={badgeForStage("unmatched")}>Unmatched</span>
                </div>
            </div>

            {/* Desktop table */}
            <div className={`mt-4 hidden md:block ${cardClass} overflow-hidden`}>
                <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                        <thead className="bg-slate-50">
                            <tr className="border-b border-slate-200">
                                {columns.map((col) => (
                                    <th
                                        key={col.field}
                                        onClick={() => {
                                            setCurrentPage(0);
                                            handleSort(col.field);
                                        }}
                                        className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-slate-700 cursor-pointer select-none hover:bg-slate-100"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>{col.label}</span>
                                            <span className="text-slate-400">
                                                {sortField === col.field ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                                            </span>
                                        </div>
                                    </th>
                                ))}
                                <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-slate-700">
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100">
                            {displayed.length ? (
                                displayed.map((rec) => (
                                    <tr
                                        key={rec.outreach_queue_id}
                                        className="cursor-pointer hover:bg-slate-50"
                                        onClick={() => openModal("view", rec)}
                                    >
                                        {columns.map((col, idx) => {
                                            const v = rec[col.field];
                                            const tight = idx < 3 ? "max-w-[240px] truncate" : "";
                                            if (col.field === "business_email") {
                                                return (
                                                    <td key={col.field} className={`px-4 py-3 text-sm ${tight}`}>
                                                        <div className="min-w-0">
                                                            <div className="truncate text-slate-900 font-semibold">{v ?? "-"}</div>
                                                            <div className="mt-0.5 text-xs text-slate-500">
                                                                {rec.business_email_status || "—"}
                                                            </div>
                                                        </div>
                                                    </td>
                                                );
                                            }
                                            if (col.field === "stage") {
                                                return (
                                                    <td key={col.field} className="px-4 py-3 text-sm">
                                                        <span className={badgeForStage(v)}>{v || "n/a"}</span>
                                                    </td>
                                                );
                                            }
                                            if (col.field === "status") {
                                                return (
                                                    <td key={col.field} className="px-4 py-3 text-sm">
                                                        <span className={badgeForStatus(v)}>{v || "n/a"}</span>
                                                    </td>
                                                );
                                            }
                                            if (col.field === "outcome") {
                                                return (
                                                    <td key={col.field} className="px-4 py-3 text-sm">
                                                        <span className={badgeForOutcome(v)}>{v || "—"}</span>
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td key={col.field} className={`px-4 py-3 text-sm text-slate-800 ${tight}`}>
                                                    {v ?? "-"}
                                                </td>
                                            );
                                        })}

                                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    className={iconBtn}
                                                    title="Update"
                                                    aria-label="Update"
                                                    onClick={() => openModal("update", rec)}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                {/* <button
                                                    className={`${iconBtn} border-rose-200 text-rose-700 hover:bg-rose-50`}
                                                    title="Cancel"
                                                    aria-label="Cancel"
                                                    onClick={() => openModal("cancel", rec)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button> */}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td className="px-4 py-10 text-center text-sm text-slate-500" colSpan={columns.length + 1}>
                                        {loading ? (
                                            <span className="inline-flex items-center gap-2">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Loading...
                                            </span>
                                        ) : (
                                            "No data"
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile cards */}
            <div className="mt-4 block md:hidden space-y-3">
                {displayed.length ? (
                    displayed.map((rec) => (
                        <div
                            key={rec.outreach_queue_id}
                            className={`${cardClass} p-4`}
                            onClick={() => openModal("view", rec)}
                            role="button"
                            tabIndex={0}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="truncate text-sm font-extrabold text-slate-900">
                                        {rec.contact_name || "—"}
                                    </div>
                                    <div className="mt-1 text-sm text-slate-700 truncate">{rec.company_name || "—"}</div>
                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                        <span className={badgeForStage(rec.stage)}>{rec.stage || "n/a"}</span>
                                        <span className={badgeForStatus(rec.status)}>{rec.status || "n/a"}</span>
                                        <span className={badgeForOutcome(rec.outcome)}>{rec.outcome || "—"}</span>
                                    </div>
                                </div>
                                <div className="shrink-0 text-right">
                                    <div className="text-xs font-semibold text-slate-500">Score</div>
                                    <div className="text-sm font-extrabold text-slate-900">{rec.domain_outreach_score ?? "—"}</div>
                                </div>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                <div className="col-span-2">
                                    <div className="text-xs font-semibold text-slate-500">Business email</div>
                                    <div className="text-slate-800 truncate">{rec.business_email ?? "—"}</div>
                                    <div className="text-xs text-slate-500 truncate">{rec.business_email_status ?? "—"}</div>
                                </div>
                                <div className="col-span-2">
                                    <div className="text-xs font-semibold text-slate-500">Profile URL</div>
                                    {rec.profile_url ? (
                                        <a
                                            href={rec.profile_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 text-slate-900 font-semibold hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <span className="truncate">{rec.profile_url}</span>
                                            <ExternalLink className="h-4 w-4 text-slate-400" />
                                        </a>
                                    ) : (
                                        <div className="text-slate-800">—</div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <button className={iconBtn} onClick={() => openModal("update", rec)} title="Update" aria-label="Update">
                                    <Pencil className="h-4 w-4" />
                                </button>
                                {/* <button
                                    className={`${iconBtn} border-rose-200 text-rose-700 hover:bg-rose-50`}
                                    onClick={() => openModal("cancel", rec)}
                                    title="Cancel"
                                    aria-label="Cancel"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button> */}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className={`${cardClass} p-8 text-center text-sm text-slate-500`}>
                        {loading ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading...
                            </span>
                        ) : (
                            "No data"
                        )}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
                <ReactPaginate
                    previousLabel={<span className="px-3 py-1">← Prev</span>}
                    nextLabel={<span className="px-3 py-1">Next →</span>}
                    pageCount={pageCount}
                    onPageChange={({ selected }) => setCurrentPage(selected)}
                    containerClassName="flex justify-center items-center mt-6 gap-2"
                    pageClassName="px-3 py-1 border border-slate-200 rounded-xl bg-white text-slate-700 hover:bg-slate-50"
                    activeClassName="!bg-slate-900 !text-white !border-slate-900 shadow-sm"
                    disabledClassName="opacity-50 cursor-not-allowed"
                    breakClassName="px-3 py-1 text-slate-500"
                    renderOnZeroPageCount={null}
                />
            )}

            {/* View Modal */}
            <ReactModal
                isOpen={modalType === "view"}
                onRequestClose={closeModal}
                className="bg-white p-6 rounded-2xl shadow-xl max-w-3xl w-[95vw] max-h-[85vh] overflow-y-auto mx-auto my-10 border border-slate-200"
                overlayClassName="fixed inset-0 bg-slate-900/40 flex justify-center items-center p-4 z-50"
                ariaHideApp={false}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-extrabold text-slate-900">Cold outreach lead</h2>
                        <p className="mt-1 text-sm text-slate-600">Read-only details (contact + outreach).</p>
                    </div>
                    <button
                        onClick={closeModal}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                        aria-label="Close"
                        title="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">{modalRecord && renderFields(modalRecord, false)}</div>

                <div className="mt-6 flex justify-end">
                    <button onClick={closeModal} className={subtleBtn}>
                        Close
                    </button>
                </div>
            </ReactModal>

            {/* Update Modal */}
            <ReactModal
                isOpen={modalType === "update"}
                onRequestClose={closeModal}
                className="bg-white p-6 rounded-2xl shadow-xl max-w-3xl w-[95vw] max-h-[85vh] overflow-y-auto mx-auto my-10 border border-slate-200"
                overlayClassName="fixed inset-0 bg-slate-900/40 flex justify-center items-center p-4 z-50"
                ariaHideApp={false}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-extrabold text-slate-900">Update outreach stage</h2>
                        <p className="mt-1 text-sm text-slate-600">Only stage/outcome are editable here.</p>
                    </div>
                    <button
                        onClick={closeModal}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                        aria-label="Close"
                        title="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">{modalRecord && renderFields(modalRecord, true)}</div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button onClick={closeModal} className={subtleBtn}>
                        Cancel
                    </button>
                    <button onClick={handleUpdate} className={warnBtn}>
                        <Pencil className="h-4 w-4" />
                        Update
                    </button>
                </div>
            </ReactModal>

            {/* Cancel Modal */}
            <ReactModal
                isOpen={modalType === "cancel"}
                onRequestClose={closeModal}
                className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-[95vw] mx-auto my-auto border border-slate-200"
                overlayClassName="fixed inset-0 bg-slate-900/40 flex justify-center items-center p-4 z-50"
                ariaHideApp={false}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-extrabold text-slate-900">Cancel Job</h2>
                        <p className="mt-1 text-sm text-slate-600">This will mark the job as cancelled.</p>
                    </div>
                    <button
                        onClick={closeModal}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                        aria-label="Close"
                        title="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button onClick={closeModal} className={subtleBtn}>
                        Cancel
                    </button>
                    <button onClick={handleCancel} className={dangerBtn}>
                        <Trash2 className="h-4 w-4" />
                        Cancel Job
                    </button>
                </div>
            </ReactModal>
        </>
    );
}
